import { inject, Injectable, signal, WritableSignal } from '@angular/core';

import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import { Router } from '@angular/router';
import { get, set } from 'lodash';
import { of } from 'rxjs';
import {
    AppendDashboardCachePayload,
    AppendDashboardCachePayloadSchema,
    AppendDashboardDataPayload,
    AppendDashboardDataSchemaPayloadSchema,
    CloseSection,
    CloseSectionSchema,
    NavigateToPath,
    NavigateToPathSchema,
    NavigateToUrl,
    NavigateToUrlSchema,
    OpenSection,
    OpenSectionSchema,
    ToggleDashboardCacheCache,
    UpdateComponent,
    UpdateComponentSchema,
    updateControlByControlId,
    updateControlByControlIdSchema,
    UpdateControlProps,
    UpdateDashboardCache,
    UpdateDashboardCacheSchema,
    UpdateProps,
    UpdatePropsSchema,
    UpdateSection,
    UpdateSectionSchema,
} from './iq-builder-state-actions-dashboard-models';
import { IqBuilderStateStatusService } from './iq-builder-state-status.service';

@Injectable({
    providedIn: 'root',
})
export class IqBuilderStateActionsDashboardService {
    private router = inject(Router);

    private _status: IqBuilderStateStatusService = inject(
        IqBuilderStateStatusService,
    );
    private readonly _eventBroker: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    private _registered: WritableSignal<boolean> = signal(false);

    updateComponent(payload: UpdateComponent) {
        const validatedPayload = UpdateComponentSchema.safeParse(payload);

        if (!validatedPayload.success) return;

        this._status.updateComponent(
            payload.dashboardId,
            payload.sectionId,
            payload.componentId,
            payload.component,
        );
    }

    updateDashboardCache(payload: UpdateDashboardCache): void {
        const validatedPayload = UpdateDashboardCacheSchema.safeParse(payload);
        if (!validatedPayload.success) return;

        this._status.updateDashboardCache(payload.id, payload.data);
    }

    toggleDashboardCache(payload: ToggleDashboardCacheCache): void {
        const validatedPayload = UpdateDashboardCacheSchema.safeParse(payload);

        if (!validatedPayload.success) return;

        const currentCache = {
            ...this._status.getDashboardCache(payload.id)(),
        };
        const oldValue = get(currentCache, payload.path);
        const newValue = !oldValue;
        const updatedCache = set({ ...currentCache }, payload.path, newValue);

        this._status.updateDashboardCache(payload.id, updatedCache);
    }

    updateProps(payload: UpdateProps) {
        const validatedPayload = UpdatePropsSchema.safeParse(payload);

        if (!validatedPayload.success) return;

        this._status.updateProps(
            payload.dashboardId,
            payload.sectionId,
            payload.componentId,
            payload.props,
        );
    }

    updateControlProps(payload: UpdateControlProps) {
        const validatedPayload = UpdatePropsSchema.safeParse(payload);

        if (!validatedPayload.success) return;

        this._status.updateControlProps(
            payload.dashboardId,
            payload.sectionId,
            payload.componentId,
            payload.controlId,
            payload.props,
        );
    }

    updateSection(payload: UpdateSection) {
        const validatedPayload = UpdateSectionSchema.safeParse(payload);

        if (!validatedPayload.success) return;

        this._status.updateSection(
            payload.dashboardId,
            payload.sectionId,
            payload.section,
        );
    }

    openSection(payload: OpenSection) {
        const validatedPayload = OpenSectionSchema.safeParse(payload);

        if (!validatedPayload.success) return;

        this._status.updateSection(payload.dashboardId, payload.sectionId, {
            collapsed: false,
        });
    }
    closeSection(payload: CloseSection) {
        const validatedPayload = CloseSectionSchema.safeParse(payload);

        if (!validatedPayload.success) return;

        this._status.updateSection(payload.dashboardId, payload.sectionId, {
            collapsed: true,
        });
    }

    appendDashboardData(payload: AppendDashboardDataPayload) {
        const validatedPayload =
            AppendDashboardDataSchemaPayloadSchema.safeParse(payload);

        if (!validatedPayload.success) return;

        this._status.appendDashboardData(
            payload.dashboardId,
            payload.key,
            payload.data,
        );
    }

    appendDashboardCache(payload: AppendDashboardCachePayload) {
        const validatedPayload =
            AppendDashboardCachePayloadSchema.safeParse(payload);

        if (!validatedPayload.success) return;

        this._status.appendDashboardCache(
            payload.dashboardId,
            payload.key,
            payload.data,
            payload.dataType,
        );
    }

    updateControlByControlId(payload: { controlId?: string; control?: any }) {
        const validatedPayload =
            updateControlByControlIdSchema.safeParse(payload);

        if (!validatedPayload.success) return;

        this._status.updateControlByControlId(
            payload.controlId,
            payload.control,
        );
    }

    navigateToUrl(payload: NavigateToUrl): void {
        const validatedPayload = NavigateToUrlSchema.safeParse(payload);

        if (!validatedPayload.success) return;

        payload?.newTab
            ? window.open(payload?.url, '_blank')
            : (window.location.href = payload?.url);
    }

    navigateToPath(payload: NavigateToPath): void {
        const validatedPayload = NavigateToPathSchema.safeParse(payload);
        if (!validatedPayload.success) return;

        this.router.navigate(payload.path);
    }

    register() {
        if (this._registered()) {
            return;
        }
        this._eventBroker.registerMap({
            navigateToUrl: {
                callback: (payload: NavigateToUrl) => {
                    this.navigateToUrl(payload);
                },
            },
            navigateToPath: {
                callback: (payload: NavigateToPath) => {
                    this.navigateToPath(payload);
                },
            },
            updateDashboardCache: {
                callback: (payload: UpdateDashboardCache) => {
                    this.updateDashboardCache(payload);
                },
            },
            toggleDashboardCache: {
                callback: (payload: ToggleDashboardCacheCache) => {
                    this.toggleDashboardCache(payload);
                },
            },
            updateProps: {
                callback: (payload: UpdateProps) => {
                    this.updateProps(payload);
                },
            },
            updateControlProps: {
                callback: (payload: UpdateControlProps) => {
                    this.updateControlProps(payload);
                },
            },
            updateComponent: {
                callback: (payload: UpdateComponent) => {
                    this.updateComponent(payload);
                },
            },
            updateSection: {
                callback: (payload: UpdateSection) => {
                    this.updateSection(payload);
                },
            },
            appendDashboardData: {
                callback: (payload: AppendDashboardDataPayload) => {
                    this.appendDashboardData(payload);
                },
            },
            appendDashboardCache: {
                callback: (payload: AppendDashboardCachePayload) => {
                    this.appendDashboardCache(payload);
                },
            },
            updateControlByControlId: {
                callback: (payload: updateControlByControlId) => {
                    this.updateControlByControlId(payload);
                },
            },
            'type:section:open': {
                callback: (payload: OpenSection) => {
                    this.openSection(payload);
                },
            },
            'type:section:close': {
                callback: (payload: CloseSection) => {
                    this.closeSection(payload);
                },
            },
        });

        this._registered.set(true);
    }
}
